// admin-lifecycle.js
//
// Operation / generation / abort-wait helpers shared by every restyled admin
// screen (Dashboard, Feature Flags, Rooms, Database). These are the correctness
// foundation of the Task 3.1 "screen-lifecycle idiom" (plan Step 3c): one
// AbortController + one deadline per operation, a per-lane generation token that
// supersedes stale in-flight work, and a cancelable promise that lets an
// aggregate await race against the signal so a signal-ignoring dependency can
// never hang the operation past its deadline. See admin-validate.js for the
// companion response-shape validation + failure-mapping (`validateShape`/`toError`).

// The default per-operation deadline. Exported so the router can DERIVE its own
// handler backstop from it (HANDLER_DEADLINE_MS = this + margin) rather than
// hard-coding a second literal that could silently drift below it.
export const DEFAULT_OP_DEADLINE_MS = 15000;

// Standard abort-reason factories, shared by every screen's teardown. The
// 'navigation'/'aborted' reason strings are load-bearing — admin-validate.js
// `toError` classifies a cancellation by `e.message === 'navigation'` — so they
// live in ONE place rather than duplicated per screen.
export const navAbort = () => new DOMException('navigation', 'AbortError');
export const abortErr = () => new DOMException('aborted', 'AbortError');

export function createOperation(deadlineMs = DEFAULT_OP_DEADLINE_MS) {
  const controller = new AbortController();
  let closed = false;                          // ATOMIC done-flag — true after finish() OR abort(); a continuation checks op.closed (plus the signal + generation) before starting a later stage or writing any result/status/DOM
  let timer;
  const abort = (reason) => {
    closed = true;
    clearTimeout(timer);
    controller.abort(reason);                  // AbortController preserves the FIRST reason; repeated aborts remain safe
  };
  timer = setTimeout(
    () => abort(new DOMException('deadline exceeded', 'TimeoutError')),   // deadline MUST use the same seal path (closed=true)
    deadlineMs,
  );
  return {
    signal: controller.signal,
    get closed() { return closed; },           // read-only view of the seal (belt-and-suspenders with signal.aborted)
    // finish(): stop the deadline timer once ALL of the operation's awaited work has settled.
    // Does NOT abort — the operation completed. Idempotent (clearTimeout is safe to repeat).
    finish() { closed = true; clearTimeout(timer); },
    // abort(reason): cancel the operation NOW, PRESERVING a discriminated reason so the sweep can
    // classify it — a deadline passes a TimeoutError; a navigation/supersession teardown passes a
    // DOMException('navigation','AbortError') (see Task 1.5 persistent teardown); an exceptional
    // orchestration exit passes its cause. Idempotent, and still works AFTER finish() (abort() is
    // safe to repeat), so an exceptional early exit with outstanding parallel work still cancels it.
    abort,
  };
}

export function createGeneration() {
  let g = 0;
  return { bump: () => ++g, current: () => g, isStale: (n) => n !== g };
}

// Rejects when `signal` aborts — race an aggregate await against `.promise` so a signal-IGNORING promise can't hang past the
// deadline. Returns a CANCELABLE handle: `.cancel()` removes the abort listener DETERMINISTICALLY on the success path (the race
// resolved via the other branch), rather than leaving a live listener on `signal` to be reclaimed only by GC. `.promise` carries
// a no-op `.catch` internally so that a `signal` abort arriving in the window before the caller attaches its own handler can
// never surface as an unhandledrejection.
export function whenAborted(signal) {
  const remover = new AbortController();   // aborting THIS controller auto-removes our listener (addEventListener `signal` option)
  const promise = new Promise((_, reject) => {
    const fail = () => reject(signal.reason ?? new DOMException('aborted', 'AbortError'));
    if (signal.aborted) fail(); else signal.addEventListener('abort', fail, { once: true, signal: remover.signal });
  });
  promise.catch(() => {});                 // pre-attach so a post-race abort can't become an unhandledrejection
  return { promise, cancel: () => remover.abort() };
}
